// Baselayer Background Checks — TanStack Query hooks for the background-check
// list / detail surfaces. Stable query-key factory + mutation invalidation,
// mirroring the @/hooks/portfolioAdmin conventions.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  backgroundChecksApi,
  type BackgroundCheckDetailDto,
  type BackgroundCheckDto,
  type BackgroundChecksListParams,
  type FilePdfRequest,
  type RunBackgroundCheckRequest,
} from "@/api/backgroundChecks/backgroundChecksApi";
import { invalidateDmsDocumentScope } from "@/hooks/portfolioAdmin/invalidation";

// ============================== query keys ============================

export const backgroundCheckKeys = {
  all: ["background-checks"] as const,
  list: (params?: BackgroundChecksListParams) =>
    [
      "background-checks",
      "list",
      params?.dealId ?? null,
      params?.status ?? null,
      params?.type ?? null,
      params?.subject ?? null,
    ] as const,
  detail: (id: string) => ["background-checks", "detail", id] as const,
};

// ============================== queries ===============================

export function useBackgroundChecks(params?: BackgroundChecksListParams) {
  return useQuery<BackgroundCheckDto[]>({
    queryKey: backgroundCheckKeys.list(params),
    queryFn: () => backgroundChecksApi.list(params),
  });
}

export function useBackgroundCheck(id: string | undefined) {
  return useQuery<BackgroundCheckDetailDto>({
    queryKey: backgroundCheckKeys.detail(id ?? ""),
    queryFn: () => backgroundChecksApi.get(id!),
    enabled: !!id,
  });
}

// ============================== mutations =============================

export function useRunBackgroundCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RunBackgroundCheckRequest) =>
      backgroundChecksApi.run(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: backgroundCheckKeys.all });
    },
  });
}

/** Manual authoritative re-fetch from Baselayer. Takes the check id at
 *  mutate time so one instance can serve a whole table of rows. */
export function useRefreshBackgroundCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => backgroundChecksApi.refresh(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: backgroundCheckKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ["background-checks", "list"] });
    },
  });
}

export function useFileBackgroundCheckPdf(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FilePdfRequest) => backgroundChecksApi.filePdf(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: backgroundCheckKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ["background-checks", "list"] });
      // The filed PDF lands in the deal's DMS folder.
      void invalidateDmsDocumentScope(qc);
    },
  });
}
